import * as _ from "lodash";

export const CUSTOM_DISCOUNT_FIXED_TYPE = "fixed";
export const CUSTOM_DISCOUNT_PERCENT_TYPE = "percent";
export const CUSTOM_DISCOUNT_PERCENT_PRESETS = [20, 30, 50, 95];
export const CUSTOM_DISCOUNT_FIXED_PRESETS = [2, 5, 10, 20];
export const CUSTOM_DISCOUNT_PERCENT_MIN = 1;
export const CUSTOM_DISCOUNT_PERCENT_MAX = 99;
export const CUSTOM_DISCOUNT_PERCENT_STEP = 1;
export const CUSTOM_DISCOUNT_FIXED_MIN = 0.1;
export const CUSTOM_DISCOUNT_FIXED_STEP = 0.1;

function getCheckboxPromotionIdentifier(promo: any) {
  if (!promo) {
    return null;
  }

  return (
    promo.id ||
    promo.promo_id ||
    promo.promotion_id ||
    promo.promotionId ||
    null
  );
}

function sanitizeCustomDiscountValue(value: any) {
  if (_.isNil(value) || value === "") {
    return null;
  }

  const parsedValue = parseFloat(value);

  if (_.isNaN(parsedValue)) {
    return null;
  }

  return parsedValue;
}

function roundFixedCustomDiscountValue(value: number) {
  return Math.round(value * 10) / 10;
}

function clampPercentCustomDiscountValue(value: any) {
  const parsedValue = sanitizeCustomDiscountValue(value);

  if (_.isNil(parsedValue)) {
    return null;
  }

  return _.clamp(
    Math.round(parsedValue),
    CUSTOM_DISCOUNT_PERCENT_MIN,
    CUSTOM_DISCOUNT_PERCENT_MAX
  );
}

export function getFixedCustomDiscountMaxValue(maxAmount: any) {
  const parsedMaxAmount = sanitizeCustomDiscountValue(maxAmount);

  if (_.isNil(parsedMaxAmount) || parsedMaxAmount <= 0) {
    return null;
  }

  return Math.floor(parsedMaxAmount * 10) / 10;
}

function clampFixedCustomDiscountValue(value: any, grandTotal: any) {
  const parsedValue = sanitizeCustomDiscountValue(value);
  const fixedMaxValue = getFixedCustomDiscountMaxValue(grandTotal);

  if (_.isNil(parsedValue) || _.isNil(fixedMaxValue)) {
    return null;
  }

  return roundFixedCustomDiscountValue(
    _.clamp(parsedValue, CUSTOM_DISCOUNT_FIXED_MIN, fixedMaxValue)
  );
}

function inferCustomDiscountType(promo: any) {
  if (
    promo.discountType === CUSTOM_DISCOUNT_FIXED_TYPE ||
    promo.discountType === CUSTOM_DISCOUNT_PERCENT_TYPE
  ) {
    return promo.discountType;
  }

  if (!_.isNil(sanitizeCustomDiscountValue(promo.discountPercentage))) {
    return CUSTOM_DISCOUNT_PERCENT_TYPE;
  }

  if (!_.isNil(sanitizeCustomDiscountValue(promo.fixedAmount))) {
    return CUSTOM_DISCOUNT_FIXED_TYPE;
  }

  return CUSTOM_DISCOUNT_FIXED_TYPE;
}

export function normalizeCheckboxPromotion(promo: any) {
  const normalizedPromotion: any = _.cloneDeep(promo);

  if (
    !normalizedPromotion ||
    typeof normalizedPromotion !== "object" ||
    !normalizedPromotion.custom_discount
  ) {
    return normalizedPromotion;
  }

  normalizedPromotion.discountType =
    inferCustomDiscountType(normalizedPromotion);

  if (normalizedPromotion.discountType === CUSTOM_DISCOUNT_PERCENT_TYPE) {
    normalizedPromotion.discountPercentage = sanitizeCustomDiscountValue(
      normalizedPromotion.discountPercentage
    );
    normalizedPromotion.fixedAmount = null;
  } else {
    normalizedPromotion.discountType = CUSTOM_DISCOUNT_FIXED_TYPE;
    normalizedPromotion.fixedAmount = sanitizeCustomDiscountValue(
      normalizedPromotion.fixedAmount
    );
    normalizedPromotion.discountPercentage = null;
  }

  return normalizedPromotion;
}

export function initializeCustomDiscountPromotion(promo: any) {
  const normalizedPromotion: any = _.cloneDeep(promo);

  if (!normalizedPromotion || !normalizedPromotion.custom_discount) {
    return normalizeCheckboxPromotion(normalizedPromotion);
  }

  normalizedPromotion.discountType = CUSTOM_DISCOUNT_PERCENT_TYPE;
  normalizedPromotion.fixedAmount = null;
  normalizedPromotion.discountPercentage = null;

  return normalizeCheckboxPromotion(normalizedPromotion);
}

export function changeCustomDiscountPromotionType(promo: any, discountType: any) {
  const normalizedPromotion: any = _.cloneDeep(promo);

  if (!normalizedPromotion || !normalizedPromotion.custom_discount) {
    return normalizeCheckboxPromotion(normalizedPromotion);
  }

  normalizedPromotion.discountType =
    discountType === CUSTOM_DISCOUNT_FIXED_TYPE
      ? CUSTOM_DISCOUNT_FIXED_TYPE
      : CUSTOM_DISCOUNT_PERCENT_TYPE;
  normalizedPromotion.fixedAmount = null;
  normalizedPromotion.discountPercentage = null;

  return normalizeCheckboxPromotion(normalizedPromotion);
}

export function applyCustomDiscountPresetValue(
  promo: any,
  value: number,
  grandTotal: any
) {
  const normalizedPromotion: any = _.cloneDeep(promo);

  if (!normalizedPromotion || !normalizedPromotion.custom_discount) {
    return normalizeCheckboxPromotion(normalizedPromotion);
  }

  if (normalizedPromotion.discountType === CUSTOM_DISCOUNT_FIXED_TYPE) {
    normalizedPromotion.fixedAmount = clampFixedCustomDiscountValue(
      value,
      grandTotal
    );
    normalizedPromotion.discountPercentage = null;
  } else {
    normalizedPromotion.discountType = CUSTOM_DISCOUNT_PERCENT_TYPE;
    normalizedPromotion.discountPercentage =
      clampPercentCustomDiscountValue(value);
    normalizedPromotion.fixedAmount = null;
  }

  return normalizeCheckboxPromotion(normalizedPromotion);
}

export function stepCustomDiscountPromotionValue(
  promo: any,
  direction: number,
  grandTotal: any
) {
  const normalizedPromotion: any = _.cloneDeep(promo);

  if (!normalizedPromotion || !normalizedPromotion.custom_discount) {
    return normalizeCheckboxPromotion(normalizedPromotion);
  }

  if (normalizedPromotion.discountType === CUSTOM_DISCOUNT_FIXED_TYPE) {
    const fixedMaxValue = getFixedCustomDiscountMaxValue(grandTotal);

    if (_.isNil(fixedMaxValue) || fixedMaxValue < CUSTOM_DISCOUNT_FIXED_MIN) {
      return normalizeCheckboxPromotion(normalizedPromotion);
    }

    const currentValue = sanitizeCustomDiscountValue(
      normalizedPromotion.fixedAmount
    );
    const nextValue = _.isNil(currentValue)
      ? CUSTOM_DISCOUNT_FIXED_MIN
      : currentValue + direction * CUSTOM_DISCOUNT_FIXED_STEP;

    normalizedPromotion.fixedAmount = clampFixedCustomDiscountValue(
      nextValue,
      grandTotal
    );
    normalizedPromotion.discountPercentage = null;
  } else {
    normalizedPromotion.discountType = CUSTOM_DISCOUNT_PERCENT_TYPE;

    const currentValue = sanitizeCustomDiscountValue(
      normalizedPromotion.discountPercentage
    );
    const nextValue = _.isNil(currentValue)
      ? CUSTOM_DISCOUNT_PERCENT_MIN
      : currentValue + direction * CUSTOM_DISCOUNT_PERCENT_STEP;

    normalizedPromotion.discountPercentage =
      clampPercentCustomDiscountValue(nextValue);
    normalizedPromotion.fixedAmount = null;
  }

  return normalizeCheckboxPromotion(normalizedPromotion);
}

export function normalizeCheckboxPromotions(checkboxPromotions: any) {
  if (!_.isArray(checkboxPromotions)) {
    return [];
  }

  return _.map(checkboxPromotions, (promo: any) =>
    normalizeCheckboxPromotion(promo)
  );
}

export function mergeCheckboxPromotionsWithLocalState(
  serverCheckboxPromotions: any,
  localCheckboxPromotions: any
) {
  if (!_.isArray(serverCheckboxPromotions)) {
    return [];
  }

  const normalizedLocalCheckboxPromotions = normalizeCheckboxPromotions(
    localCheckboxPromotions
  );

  return _.map(serverCheckboxPromotions, (serverPromotion: any) => {
    const mergedPromotion: any = _.cloneDeep(serverPromotion);
    const matchingLocalPromotion = _.find(
      normalizedLocalCheckboxPromotions,
      (localPromotion: any) =>
        getCheckboxPromotionIdentifier(localPromotion) ===
        getCheckboxPromotionIdentifier(mergedPromotion)
    );

    if (
      matchingLocalPromotion &&
      (matchingLocalPromotion.custom_discount || mergedPromotion.custom_discount)
    ) {
      mergedPromotion.discountType = matchingLocalPromotion.discountType;
      mergedPromotion.fixedAmount = matchingLocalPromotion.fixedAmount;
      mergedPromotion.discountPercentage =
        matchingLocalPromotion.discountPercentage;
    }

    return normalizeCheckboxPromotion(mergedPromotion);
  });
}

export function persistCheckboxPromotions(checkboxPromotions: any) {
  if (!navigator.cookieEnabled) {
    return;
  }

  window.localStorage.setItem(
    "checkboxPromotions",
    JSON.stringify(normalizeCheckboxPromotions(checkboxPromotions))
  );
}
